import React, { useState, useMemo } from 'react';
import styles from './ResourcePage.module.css';
import SearchBar from './SearchBar';

const ResourcePage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // SVG Icons for categories
  const getCategoryIcon = (category) => {
    const iconStyle = { width: '16px', height: '16px', marginRight: '6px', fill: 'currentColor' };
    
    switch (category) {
      case 'scholarship':
        return (
          <svg style={iconStyle} viewBox="0 0 24 24">
            <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
          </svg>
        );
      case 'research':
        return (
          <svg style={iconStyle} viewBox="0 0 24 24">
            <path d="M9.5 3A6.5 6.5 0 0 1 16 9.5c0 1.61-.59 3.09-1.56 4.23l.27.27h.79l5 5-1.5 1.5-5-5v-.79l-.27-.27A6.516 6.516 0 0 1 9.5 16 6.5 6.5 0 0 1 3 9.5 6.5 6.5 0 0 1 9.5 3m0 2C7 5 5 7 5 9.5S7 14 9.5 14 14 12 14 9.5 12 5 9.5 5z"/>
            <circle cx="9.5" cy="9.5" r="1.5"/>
          </svg>
        );
      case 'internship':
        return (
          <svg style={iconStyle} viewBox="0 0 24 24">
            <path d="M10 2h4a2 2 0 0 1 2 2v2h4a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4V4a2 2 0 0 1 2-2zm4 4V4h-4v2h4zm6 3H4v10h16V9z"/>
          </svg>
        );
      case 'test-prep':
        return (
          <svg style={iconStyle} viewBox="0 0 24 24">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11zm-5.5-8.5L9 15l-2-2-1.5 1.5L9 18l6-6-1.5-1.5z"/>
          </svg>
        );
      default:
        return null;
    }
  };

  // Current Summer 2025 resource data for HIGH SCHOOL STUDENTS
  const resourceData = [
    // HIGH-VALUE SCHOLARSHIPS
    {
      id: 1,
      title: "Be Bold Scholarship",
      category: "scholarship",
      type: "merit-based",
      amount: "$25,000",
      deadline: "Rolling monthly deadlines, awards December 1, 2025",
      description: "No-essay scholarship for students with the boldest profile. Open to high school and college students.",
      requirements: ["Bold profile demonstrating unique achievements", "Must be enrolled in high school or college", "Submit compelling profile"],
      organization: "Bold.org",
      tags: ["no-essay", "high-value", "merit-based", "high-school"],
      link: "https://bold.org/scholarships/the-be-bold-no-essay-scholarship/"
    },
    {
      id: 2,
      title: "Coca-Cola Scholars Program",
      category: "scholarship",
      type: "leadership",
      amount: "$20,000",
      deadline: "Application opens August 1, 2025, deadline September 30, 2025",
      description: "Leadership-focused scholarship for high school seniors (Class of 2026). Recognizes academic excellence and community leadership.",
      requirements: ["High school senior (Class of 2026)", "Minimum 3.0 GPA", "Leadership experience", "Community service"],
      organization: "Coca-Cola Scholars Foundation",
      tags: ["leadership", "community-service", "high-value", "class-of-2026"],
      link: "https://www.coca-colascholarsfoundation.org/apply/"
    },
    {
      id: 3,
      title: "AbbVie Immunology Scholarship",
      category: "scholarship",
      type: "health-based",
      amount: "$20,000",
      deadline: "June 10, 2026",
      description: "For students with chronic inflammatory diseases pursuing higher education in any field.",
      requirements: ["Diagnosed chronic inflammatory disease", "Pursuing post-secondary education", "Financial need consideration"],
      organization: "AbbVie Inc.",
      tags: ["health-condition", "chronic-illness", "high-value", "inclusive"],
      link: "https://www.scholarshipamerica.org/"
    },
    {
      id: 4,
      title: "Scholarships360 No Essay Scholarship",
      category: "scholarship",
      type: "no-essay",
      amount: "$10,000",
      deadline: "Rolling applications",
      description: "Open to all students seeking education funding. No essay required, simple application process.",
      requirements: ["Enrolled in or planning to attend college", "Simple online application", "No essay required"],
      organization: "Scholarships360",
      tags: ["no-essay", "simple-application", "rolling-deadline", "accessible"],
      link: "https://scholarships360.org/"
    },
    {
      id: 5,
      title: "Marine Band Concerto Competition",
      category: "scholarship",
      type: "talent-based",
      amount: "$2,500",
      deadline: "November 15, 2025",
      description: "For high school musicians demonstrating exceptional musical talent and performance ability.",
      requirements: ["High school student", "Advanced musical ability", "Performance audition", "US citizen or permanent resident"],
      organization: "U.S. Marine Band",
      tags: ["music", "performance", "talent", "competition"],
      link: "https://www.marineheritage.org/"
    },
    {
      id: 6,
      title: "Rover College Scholarship",
      category: "scholarship",
      type: "essay-based",
      amount: "$2,500",
      deadline: "May 1, 2026",
      description: "Requires a 400-500 word essay about pets and their impact on your life.",
      requirements: ["400-500 word pet essay", "High school or college student", "Pet ownership experience helpful"],
      organization: "Rover.com",
      tags: ["essay", "pets", "creative-writing", "accessible"],
      link: "https://www.rover.com/scholarships/"
    },
    {
      id: 7,
      title: "Niche $10,000 Scholarship",
      category: "scholarship",
      type: "no-essay",
      amount: "$10,000",
      deadline: "Monthly drawings",
      description: "No essay required scholarship for college expenses including tuition and housing.",
      requirements: ["No essay required", "Simple registration", "Monthly drawings", "College-bound students"],
      organization: "Niche.com",
      tags: ["no-essay", "monthly", "tuition", "housing"],
      link: "https://www.niche.com/colleges/scholarship/no-essay-scholarship/"
    },
    {
      id: 8,
      title: "ScholarshipPoints Monthly",
      category: "scholarship",
      type: "no-essay",
      amount: "Various amounts",
      deadline: "Monthly opportunities",
      description: "Monthly no-essay scholarship opportunities for students ages 16+ who are US residents.",
      requirements: ["Age 16+", "US resident", "Active account participation", "Monthly entry"],
      organization: "ScholarshipPoints",
      tags: ["monthly", "age-16+", "no-essay", "US-residents"],
      link: "https://www.scholarshippoints.com/"
    },

    // HIGH SCHOOL INTERNSHIPS - GOVERNMENT & STEM
    {
      id: 9,
      title: "NASA OSTEM Internships",
      category: "internship",
      type: "government",
      amount: "Paid internship",
      deadline: "Summer 2026: February 27, 2026, Fall 2026: May 22, 2026",
      description: "Paid internships for high school students at NASA centers. Work alongside NASA scientists and engineers.",
      requirements: ["US citizen", "Minimum 3.0 GPA", "STEM interest", "Must be 16+ years old"],
      organization: "NASA",
      tags: ["NASA", "STEM", "paid", "space", "engineering"],
      link: "https://stemgateway.nasa.gov/"
    },
    {
      id: 10,
      title: "NASA Climate Change Research Initiative",
      category: "internship",
      type: "research",
      amount: "Stipend provided",
      deadline: "Rolling applications",
      description: "~20 high school students selected to work with NASA scientists on climate change research projects.",
      requirements: ["Strong science background", "Climate science interest", "Research aptitude", "US citizen"],
      organization: "NASA",
      tags: ["climate-science", "research", "selective", "environmental"],
      link: "https://stemgateway.nasa.gov/"
    },
    {
      id: 11,
      title: "NIST Summer High School Intern Program",
      category: "internship",
      type: "government",
      amount: "8-week unpaid research program",
      deadline: "Opens November 1, 2025, deadline late January 2026",
      description: "Research internship at National Institute of Standards and Technology working on cutting-edge scientific projects.",
      requirements: ["High school junior or senior", "Strong STEM background", "US citizen", "Live near Boulder CO or Gaithersburg MD", "3.0+ GPA"],
      organization: "NIST",
      tags: ["research", "standards", "technology", "government"],
      link: "https://www.nist.gov/iaao/academic-affairs-office/high-school-students-ship"
    },
    {
      id: 12,
      title: "Air Force Research Laboratory Scholars",
      category: "internship",
      type: "government",
      amount: "Stipend-paid",
      deadline: "Early application recommended",
      description: "Summer opportunities for upper-level high school students interested in aerospace and defense research.",
      requirements: ["Upper-level high school", "US citizen", "Security clearance eligible", "STEM background"],
      organization: "U.S. Air Force Research Laboratory",
      tags: ["aerospace", "defense", "security-clearance", "selective"],
      link: "https://www.afrl.af.mil/"
    },
    {
      id: 13,
      title: "U.S. Army DEVCOM Analysis Center",
      category: "internship",
      type: "cyber",
      amount: "Paid position",
      deadline: "May-August 2025 (12-14 weeks)",
      description: "Cyber research internship for students ages 16+ interested in cybersecurity and defense technology.",
      requirements: ["Age 16+", "US citizen", "Cybersecurity interest", "Security clearance eligible"],
      organization: "U.S. Army DEVCOM",
      tags: ["cybersecurity", "defense", "research", "military"],
      link: "https://www.usaeop.com/program/high-school-internships/"
    },

    // INDUSTRY & CORPORATE INTERNSHIPS
    {
      id: 14,
      title: "Sandia National Laboratories",
      category: "internship",
      type: "research",
      amount: "Competitive stipend",
      deadline: "Rolling deadline",
      description: "R&D roles for high school students ages 16+ with minimum 3.0 GPA in various scientific disciplines.",
      requirements: ["Age 16+", "Minimum 3.0 GPA", "US citizen", "STEM background"],
      organization: "Sandia National Laboratories",
      tags: ["R&D", "national-lab", "competitive", "STEM"],
      link: "https://www.usajobs.gov/"
    },
    {
      id: 15,
      title: "Meta Summer Academy",
      category: "internship",
      type: "technology",
      amount: "Paid program",
      deadline: "Applications typically open January",
      description: "For high school sophomores in East Palo Alto, Belle Haven, North Fair Oaks, and Redwood City areas.",
      requirements: ["High school sophomore", "Specific geographic areas", "Technology interest", "Local residency"],
      organization: "Meta (Facebook)",
      tags: ["technology", "sophomores", "local", "silicon-valley"],
      link: "https://www.metacareers.com/"
    },
    {
      id: 16,
      title: "Kaiser Permanente KP LAUNCH",
      category: "internship",
      type: "healthcare",
      amount: "Paid internship",
      deadline: "Applications typically spring",
      description: "Paid healthcare internships in Oakland/Northern California for high school students interested in healthcare careers.",
      requirements: ["Northern California resident", "Healthcare interest", "High school student", "Strong academics"],
      organization: "Kaiser Permanente",
      tags: ["healthcare", "paid", "northern-california", "medical"],
      link: "https://careers.kaiserpermanente.org/"
    },

    // LOCAL/REGIONAL OPPORTUNITIES
    {
      id: 17,
      title: "Manhattan District Attorney's Office",
      category: "internship",
      type: "government",
      amount: "Educational experience",
      deadline: "Applications typically January-March",
      description: "5-week program for high school sophomores, juniors, and seniors in Manhattan interested in law and justice.",
      requirements: ["High school sophomore/junior/senior", "Manhattan resident", "Law interest", "Strong academics", "Essay required"],
      organization: "Manhattan DA's Office",
      tags: ["law", "justice", "NYC", "legal-system"],
      link: "https://www.manhattanda.org/"
    },
    {
      id: 18,
      title: "Apollo Theater Internships",
      category: "internship",
      type: "arts",
      amount: "Educational stipend",
      deadline: "Rolling applications",
      description: "Technical stage production and arts administration internships at the historic Apollo Theater in NYC.",
      requirements: ["NYC area preferred", "Arts/theater interest", "High school or college", "Creative aptitude"],
      organization: "Apollo Theater",
      tags: ["theater", "arts", "NYC", "production"],
      link: "https://www.apollotheater.org/"
    },
    {
      id: 19,
      title: "Chicago Municipal Government",
      category: "internship",
      type: "government",
      amount: "Varies by program",
      deadline: "Multiple opportunities year-round",
      description: "Various internship opportunities within Chicago city government departments.",
      requirements: ["Chicago area resident preferred", "Government interest", "High school junior/senior", "Civic engagement"],
      organization: "City of Chicago",
      tags: ["municipal", "government", "civic", "chicago"],
      link: "https://www.chicago.gov/"
    },
    {
      id: 20,
      title: "San Antonio Family Service",
      category: "internship",
      type: "non-profit",
      amount: "Paid part-time",
      deadline: "Rolling applications",
      description: "Paid part-time internships across various industries through community partnerships.",
      requirements: ["San Antonio area", "Part-time availability", "Community service interest", "Professional development focus"],
      organization: "San Antonio Family Service",
      tags: ["community-service", "part-time", "paid", "san-antonio"],
      link: "https://www.safamilyservice.org/"
    },

    // RESEARCH PROGRAMS
    {
      id: 21,
      title: "George Mason University ASSIP",
      category: "research",
      type: "university",
      amount: "Research stipend",
      deadline: "Early application recommended",
      description: "One-on-one work with professional scientists in advanced research projects.",
      requirements: ["Strong STEM background", "Research aptitude", "Commitment to full program", "Advanced coursework"],
      organization: "George Mason University",
      tags: ["university-research", "one-on-one", "advanced", "STEM"],
      link: "https://science.gmu.edu/"
    },
    {
      id: 22,
      title: "LSU Summer Research Program",
      category: "research",
      type: "health",
      amount: "Research stipend",
      deadline: "Application deadlines vary",
      description: "Public health and clinical science focus for high school students interested in medical research.",
      requirements: ["Health science interest", "Strong academics", "Research commitment", "Pre-med interest helpful"],
      organization: "Louisiana State University",
      tags: ["public-health", "clinical-science", "medical-research", "pre-med"],
      link: "https://www.lsu.edu/"
    },
    {
      id: 23,
      title: "BioBus Junior Scientists",
      category: "research",
      type: "education",
      amount: "Stipend + experience",
      deadline: "Rolling applications",
      description: "Research projects combined with teaching opportunities in mobile science education.",
      requirements: ["Science interest", "Teaching aptitude", "NYC area preferred", "Science communication skills"],
      organization: "BioBus",
      tags: ["science-education", "teaching", "mobile-lab", "communication"],
      link: "https://www.biobus.org/"
    },
    {
      id: 24,
      title: "Spark SIP Computer Science",
      category: "research",
      type: "computer-science",
      amount: "Prestigious program",
      deadline: "Highly competitive (<5% acceptance)",
      description: "Highly competitive computer science research internship with <5% acceptance rate.",
      requirements: ["Advanced CS background", "Exceptional academics", "Research experience preferred", "Strong programming skills"],
      organization: "Spark SIP",
      tags: ["computer-science", "highly-competitive", "programming", "prestigious"],
      link: "https://www.sparksip.org/"
    },
    {
      id: 25,
      title: "University of Texas at Austin REU",
      category: "research",
      type: "university",
      amount: "$6,300 stipend",
      deadline: "Applications typically open fall/winter",
      description: "9-week paid Cyberinfrastructure Research program (alternative to NSF REU programs).",
      requirements: ["STEM background", "Research interest", "Full-time commitment", "US citizen/permanent resident"],
      organization: "UT Austin",
      tags: ["cyberinfrastructure", "paid", "9-week", "computing"],
      link: "https://www.utexas.edu/"
    },
    {
      id: 26,
      title: "Chicago Quantum Exchange Fellowship",
      category: "research",
      type: "quantum",
      amount: "Fellowship stipend",
      deadline: "February 7, 2025 (may be closed)",
      description: "Quantum science focus for students ages 16-26 interested in cutting-edge physics research.",
      requirements: ["Ages 16-26", "Physics/quantum interest", "Advanced math background", "Research aptitude"],
      organization: "University of Chicago",
      tags: ["quantum-science", "physics", "advanced", "fellowship"],
      link: "https://chicagoquantum.org/"
    },

    // TEST PREP RESOURCES - 2025 DIGITAL SAT
    {
      id: 27,
      title: "Khan Academy Digital SAT Prep",
      category: "test-prep",
      type: "online",
      amount: "Free",
      deadline: "Self-paced year-round",
      description: "Free Official Digital SAT Prep with adaptive practice tailored to the new 100% digital format.",
      requirements: ["Internet access", "College Board account", "Self-motivated learning"],
      organization: "Khan Academy & College Board",
      tags: ["digital-SAT", "adaptive", "free", "official"],
      link: "https://www.khanacademy.org/digital-sat"
    },
    {
      id: 28,
      title: "Kaplan Digital SAT Prep Plus 2025",
      category: "test-prep",
      type: "comprehensive",
      amount: "$299-499",
      deadline: "Available year-round",
      description: "Full-length adaptive practice tests, 700+ questions, online access designed for new digital format.",
      requirements: ["Purchase required", "Online access", "Time commitment for practice"],
      organization: "Kaplan Test Prep",
      tags: ["digital-SAT", "adaptive-tests", "comprehensive", "700-questions"],
      link: "https://www.kaptest.com/sat"
    },
    {
      id: 29,
      title: "Princeton Review Digital SAT Premium",
      category: "test-prep",
      type: "premium",
      amount: "$399-599",
      deadline: "Available year-round",
      description: "5 practice tests (2 in book, 3 online adaptive) plus comprehensive review for 2025 digital SAT.",
      requirements: ["Purchase required", "Mixed format study", "Practice test commitment"],
      organization: "Princeton Review",
      tags: ["digital-SAT", "mixed-format", "premium", "5-tests"],
      link: "https://www.princetonreview.com/college/sat-test-prep"
    },
    {
      id: 30,
      title: "Barron's Digital SAT Study Guide",
      category: "test-prep",
      type: "study-guide",
      amount: "$199-299",
      deadline: "Available year-round",
      description: "4 practice tests plus comprehensive review designed specifically for the digital SAT format.",
      requirements: ["Purchase required", "Self-study approach", "Practice commitment"],
      organization: "Barron's Educational",
      tags: ["digital-SAT", "study-guide", "4-tests", "comprehensive"],
      link: "https://www.barronseduc.com/"
    },
    {
      id: 31,
      title: "SAT Registration & Test Dates",
      category: "test-prep",
      type: "registration",
      amount: "$60 base fee",
      deadline: "2025 dates: August 23, September 13, October 4, November 8, December 6",
      description: "Official SAT registration portal. Register early to secure preferred test locations. Late registration adds $30.",
      requirements: ["Online registration required", "Register 5-6 weeks before test date", "Photo ID required on test day"],
      organization: "College Board",
      tags: ["registration", "test-dates", "official", "digital-SAT"],
      link: "https://satsuite.collegeboard.org/sat/dates-deadlines"
    },
    {
      id: 32,
      title: "Common Application Portal",
      category: "test-prep",
      type: "application",
      amount: "Free to start",
      deadline: "Opens August 1, 2025 for 2025-26 cycle",
      description: "Central application portal for 900+ colleges. Essay prompts unchanged from previous year. Additional info section now limited to 300 words.",
      requirements: ["High school senior", "Transcript upload", "Essay required", "Letters of recommendation"],
      organization: "Common Application",
      tags: ["college-apps", "essays", "application", "2025-26"],
      link: "https://www.commonapp.org/"
    },
    {
      id: 33,
      title: "College Essay Guy Resources",
      category: "test-prep",
      type: "essay-help",
      amount: "Free resources + paid coaching",
      deadline: "Available year-round",
      description: "Free college essay resources and paid coaching services to help with Common App essays and supplemental essays.",
      requirements: ["College-bound students", "Essay writing assistance needed"],
      organization: "College Essay Guy",
      tags: ["essays", "college-apps", "coaching", "free-resources"],
      link: "https://www.collegeessayguy.com/"
    }
  ];

  // Filter categories
  const categories = [
    { value: 'scholarship', label: 'Scholarships' },
    { value: 'research', label: 'Research' },
    { value: 'internship', label: 'Internships' },
    { value: 'test-prep', label: 'Test Prep' }
  ];

  // Contextual type filters based on selected category
  const getTypeFilters = (selectedCategory) => {
    switch (selectedCategory) {
      case 'scholarship':
        return [
          { value: 'merit-based', label: 'Merit-Based' },
          { value: 'no-essay', label: 'No Essay' },
          { value: 'leadership', label: 'Leadership' },
          { value: 'health-based', label: 'Health-Based' },
          { value: 'talent-based', label: 'Talent-Based' },
          { value: 'essay-based', label: 'Essay-Based' }
        ];
      case 'research':
        return [
          { value: 'university', label: 'University Programs' },
          { value: 'quantum', label: 'Quantum Science' },
          { value: 'health', label: 'Health & Medical' },
          { value: 'education', label: 'Science Education' },
          { value: 'computer-science', label: 'Computer Science' }
        ];
      case 'internship':
        return [
          { value: 'government', label: 'Government' },
          { value: 'research', label: 'Research' },
          { value: 'technology', label: 'Technology' },
          { value: 'healthcare', label: 'Healthcare' },
          { value: 'non-profit', label: 'Non-Profit' },
          { value: 'arts', label: 'Arts & Theater' },
          { value: 'cyber', label: 'Cybersecurity' }
        ];
      case 'test-prep':
        return [
          { value: 'online', label: 'Online' },
          { value: 'comprehensive', label: 'Comprehensive' },
          { value: 'premium', label: 'Premium' },
          { value: 'study-guide', label: 'Study Guide' },
          { value: 'registration', label: 'Registration' },
          { value: 'application', label: 'College Applications' },
          { value: 'essay-help', label: 'Essay Help' }
        ];
      default:
        return [];
    }
  };

  // Get current type filters based on selected category
  const currentTypeFilters = useMemo(() => getTypeFilters(categoryFilter), [categoryFilter]);

  // Filtered resources logic
  const filteredResources = useMemo(() => {
    let filtered = resourceData;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((resource) =>
        resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resource.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resource.organization.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resource.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Apply category filter
    if (categoryFilter) {
      filtered = filtered.filter((resource) => resource.category === categoryFilter);
    }

    // Apply type filter
    if (typeFilter) {
      filtered = filtered.filter((resource) => resource.type === typeFilter);
    }

    return filtered;
  }, [searchQuery, categoryFilter, typeFilter]);

  // Handle search input changes
  const handleSearchChange = (query) => {
    setSearchQuery(query);
    
    // Generate search suggestions based on resource titles and tags
    if (query.trim()) {
      const suggestions = [];
      resourceData.forEach(resource => {
        if (resource.title.toLowerCase().includes(query.toLowerCase())) {
          suggestions.push(resource.title);
        }
        resource.tags.forEach(tag => {
          if (tag.toLowerCase().includes(query.toLowerCase()) && !suggestions.includes(tag)) {
            suggestions.push(tag);
          }
        });
      });
      setSearchSuggestions(suggestions.slice(0, 5));
      setShowSuggestions(suggestions.length > 0);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setSearchSuggestions([]);
    setShowSuggestions(false);
  };

  // Handle search input focus/blur
  const handleSearchFocus = () => {
    if (searchQuery.trim() && searchSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleSearchBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  // Handle category change - reset type filter when category changes
  const handleCategoryChange = (newCategory) => {
    setCategoryFilter(categoryFilter === newCategory ? '' : newCategory);
    setTypeFilter(''); // Reset type filter when category changes
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Summer 2025 High School Resources.</h1>
        <p className={styles.subtitle}>
          Current scholarships, internships, research opportunities, and test prep resources specifically available for high school students in Summer 2025.
        </p>
        <div className={styles.searchContainer}>
          <div className={styles.searchWrapper}>
            <SearchBar
              placeholder="Search Summer 2025 high school opportunities: scholarships, internships, research..."
              onSearchChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              value={searchQuery}
            />
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className={styles.suggestionsContainer}>
                {searchSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className={styles.suggestionItem}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className={styles.filterSection}>
        <div className={styles.filterHeader}>
          <h3>Filter Resources</h3>
          <button 
            className={styles.clearButton}
            onClick={() => {
              setCategoryFilter('');
              setTypeFilter('');
            }}
          >
            Clear All Filters
          </button>
        </div>
        
        <div className={styles.filterGroups}>
          {/* Category Filter */}
          <div className={styles.filterGroup}>
            <h4>Category</h4>
            <div className={styles.filterOptions}>
              {categories.map((category) => (
                <button
                  key={category.value}
                  className={`${styles.filterButton} ${
                    categoryFilter === category.value ? styles.filterButtonActive : ''
                  }`}
                  onClick={() => handleCategoryChange(category.value)}
                >
                  {getCategoryIcon(category.value)}
                  {category.label}
                </button>
              ))}
            </div>
          </div>

          {/* Type Filter - Only show if a category is selected */}
          {categoryFilter && currentTypeFilters.length > 0 && (
            <div className={styles.filterGroup}>
              <h4>Type</h4>
              <div className={styles.filterOptions}>
                {currentTypeFilters.map((type) => (
                  <button
                    key={type.value}
                    className={`${styles.filterButton} ${
                      typeFilter === type.value ? styles.filterButtonActive : ''
                    }`}
                    onClick={() => setTypeFilter(typeFilter === type.value ? '' : type.value)}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Section */}
      <div className={styles.resultsSection}>
        <h3>Results ({filteredResources.length} resources)</h3>
        {searchQuery && (
          <p className={styles.searchInfo}>
            Showing results for "{searchQuery}"
          </p>
        )}
        <div className={styles.resourceGrid}>
          {filteredResources.length > 0 ? (
            filteredResources.map((resource) => (
              <div key={resource.id} className={styles.resourceCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.categoryBadge} data-category={resource.category}>
                    {getCategoryIcon(resource.category)}
                    {categories.find(cat => cat.value === resource.category)?.label}
                  </div>
                  <div className={styles.amount}>{resource.amount}</div>
                </div>
                
                <h4 className={styles.resourceTitle}>{resource.title}</h4>
                <p className={styles.organization}>{resource.organization}</p>
                <p className={styles.description}>{resource.description}</p>
                
                <div className={styles.details}>
                  <div className={styles.deadline}>
                    <strong>Deadline:</strong> {resource.deadline}
                  </div>
                  
                  <div className={styles.requirements}>
                    <strong>Requirements:</strong>
                    <ul>
                      {resource.requirements.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className={styles.tags}>
                  {resource.tags.map((tag, index) => (
                    <span key={index} className={styles.tag}>
                      {tag}
                    </span>
                  ))}
                </div>
                
                <div className={styles.cardActions}>
                  <button 
                    className={styles.learnMoreButton}
                    onClick={() => window.open(resource.link, '_blank', 'noopener,noreferrer')}
                  >
                    Learn More
                  </button>
                  <button className={styles.saveButton}>Save</button>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.noResults}>
              <p>No resources found matching your criteria.</p>
              <p>Try adjusting your search or filters to find more opportunities.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResourcePage; 